import { Rank, type EnemyHookContext } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import {
  COMBAT_START,
  CYCLE,
  firstDelayAt,
  PHASE_NOYAU,
  PHASE_VERROU,
  SWEEP_LIMIT,
  TRACK_SPEED,
} from './heraut.cycle';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   B01 — LE HÉRAUT. Boss du monde 1.

   ── Ce qu'il enseigne ──
   Un boss qui n'enseigne rien n'est qu'une barre de vie (PLAN.md §6.6). Le
   Héraut enseigne une seule chose, et il la répète jusqu'à ce qu'elle soit
   comprise : LE JOUEUR PILOTE LE BOSS.

   Pendant les 2,8 premières secondes de chaque cycle, le porteur glisse vers
   l'abscisse du joueur à 190 u/s — moins de la moitié de sa vitesse. Ses bras,
   boulonnés à ±200 u, viennent donc vers lui. Puis il s'immobilise net pendant
   0,8 s, et les deux bras tirent en visée croisée depuis là où le joueur les a
   amenés.

   La conséquence se découvre en trois cycles environ : traîner le boss dans un
   coin pendant la poursuite, puis traverser pendant le verrou, rend la salve
   inoffensive. Aucun texte ne l'explique. C'est la seule pédagogie qui tienne
   dans un shmup — un cycle assez lent pour être observé, assez conséquent pour
   qu'on veuille l'observer.

   ── Structure d'un cycle (7,2 s) ──
   Le découpage complet et ses justifications vivent dans heraut.cycle.ts, qui
   est la source unique de vérité partagée par le noyau et ses six parties.
   L'essentiel : 2,0 s de FENÊTRE DE PUNITION par cycle, pendant lesquelles
   strictement rien n'est émis. C'est elle qui fait qu'un boss se combat au lieu
   de se subir, et le module la vérifie à l'import.

   ── L'armement passe par les parties ──
   Six parties, six armes : deux canons (éventails statiques), deux nacelles
   (anneaux), deux bras (visée croisée). Le noyau ne garde qu'un seul émetteur,
   délibérément le plus faible du combat — un grand éventail statique et lent,
   une volée par cycle.

   Ce résidu est un choix, pas un oubli. Sans lui, le joueur qui a démonté les
   six parties passe les trente dernières secondes du combat face à un sac de
   points de vie parfaitement inerte : la phase finale d'un boss serait sa phase
   la plus ennuyeuse, ce qui est exactement à l'envers. Avec lui, la phase
   finale devient l'examen de la leçon — le porteur poursuit toujours le joueur
   et lâche son éventail depuis juste au-dessus de lui. Celui qui a compris
   qu'il le pilote le tient à distance ; celui qui l'a subi le prend de face.

   ── Points de vie ──
   18 000 pour le noyau, 5 840 pour les six parties, soit ~23 800 au total.
   L'arme de départ fait 200 DPS (FEEL) ; avec les mods d'un monde 1 complet on
   observe 450 à 650 DPS effectifs, soit 40 à 55 s de combat. Le levier
   d'ajustement est `balance.enemyHp` — jamais l'ajout d'une arme au noyau, qui
   détruirait la fenêtre de punition.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Le hook de pilotage.
 *
 *  Échappatoire documentée (PLAN.md §3.3), et l'un des rares cas où elle est
 *  justifiée : aucune des quatre trajectoires du moteur ne sait « suivre le
 *  joueur pendant une fraction d'un cycle puis s'arrêter ». L'exprimer
 *  déclarativement demanderait une machine à états dans `MotionDef`, c'est-à-
 *  dire un mini-langage de script pour un seul client.
 *
 *  Il obéit aux mêmes règles que src/sim/ : pur, sans allocation, sans DOM,
 *  sans Math transcendante. Ici tout se ramène à des additions, des
 *  comparaisons et un `Math.floor` — le repli modulaire est écrit à la main
 *  plutôt qu'avec `%` pour rester dans le vocabulaire de src/sim/math.ts.
 *
 *  ⚠️ Le hook ne touche QUE l'abscisse. Le boss se poste à Y = +330 par sa
 *  trajectoire Hover et n'en bouge plus, ce qui garantit structurellement
 *  l'invariant de PLAN.md §6.6 : aucune émission sous Y = -180. La partie la
 *  plus basse du boss (un bras, offY -110) tire depuis Y = +220. Le bas de
 *  l'écran appartient au joueur, et rien dans ce fichier ne peut le lui
 *  reprendre par accident. */
function piloter(slot: number, age: number, dt: number, ctx: EnemyHookContext): void {
  const t = age - COMBAT_START;
  // Entrée en scène : le porteur descend droit dans l'axe, sans corriger. Une
  // poursuite pendant la descente rendrait l'arrivée illisible — le joueur doit
  // d'abord voir la silhouette qu'on lui demande de démonter.
  if (t < 0) return;

  // Repli dans [0, CYCLE). Math.floor est dans la liste blanche : arrondi exact,
  // donc identique sur tous les moteurs JS.
  const phase = t - Math.floor(t / CYCLE) * CYCLE;

  // Verrou : plus aucune correction jusqu'à la fin du cycle. L'ARRÊT DU
  // MOUVEMENT EST LE TÉLÉGRAPHE — il ne coûte ni champ ni système, et il est
  // impossible à manquer parce qu'il rompt une trajectoire que le joueur
  // regardait déjà.
  if (phase >= PHASE_VERROU) return;

  const x = ctx.x[slot]!;
  const dx = ctx.playerX - x;
  const step = TRACK_SPEED * dt;

  // Rapprochement à vitesse CONSTANTE et non exponentiel : le joueur doit
  // pouvoir prédire au pixel où sera le boss dans une seconde. Un rapprochement
  // exponentiel donnerait un mouvement plus joli et une intention illisible.
  let nx: number;
  if (dx > step) nx = x + step;
  else if (dx < -step) nx = x - step;
  else nx = ctx.playerX; // déjà à portée d'un pas : on colle, sans osciller

  // ⚠️ Bornage IMPÉRATIF. Les bras sont à ±200 u et le demi-terrain vaut 450 :
  // sans cette limite, un bras sortirait du cadre et tirerait depuis le
  // hors-champ. Rien ne rattraperait l'erreur — motionSystem repositionne une
  // partie sur son parent puis passe, donc la marge de culling ne s'applique
  // jamais à elle.
  if (nx > SWEEP_LIMIT) nx = SWEEP_LIMIT;
  else if (nx < -SWEEP_LIMIT) nx = -SWEEP_LIMIT;

  ctx.x[slot] = nx;
}

export default defineEnemy({
  id: 'heraut',
  rank: Rank.Heavy,
  boss: true,
  hp: 18000,
  visualRadius: 96,
  score: 12000,
  /* 2,2 s de télégraphe. Le boss est visible, descend, et n'est ni touchable ni
     dangereux — parties comprises, qui en héritent (spawnParts). Le rendre
     INTOUCHABLE et pas seulement inoffensif est ce qui empêche le joueur de
     grignoter une nacelle avant même d'avoir compris qu'il y en a une. */
  telegraph: 2.2,
  /* 14 ramassables d'un coup : la récompense doit être visible depuis l'autre
     bout de l'écran. Le pool en accepte 128 et les six parties ont déjà lâché
     les leurs en mourant — largement sous le plafond. */
  drops: 14,
  /* Hover : descend jusqu'à sa station puis s'y tient. Amplitude et fréquence
     à zéro — l'oscillation latérale du moteur entrerait en concurrence avec le
     hook de pilotage, et le joueur ne saurait plus si le boss réagit à lui ou
     suit sa propre courbe. Or c'est précisément ce qu'il doit pouvoir décider. */
  motion: { kind: Motion.Hover, speed: 170, amplitude: 0, frequency: 0, holdY: 330 },
  emitter: {
    // Éventail STATIQUE : le noyau ne vise jamais. Toute la menace dirigée du
    // combat passe par les bras, et disparaît avec eux.
    pattern: Pattern.Fan,
    every: CYCLE,
    count: 11,
    // 168° : un dôme quasi complet vers le bas, à 320 u/s — la plus lente
    // émission du combat avec l'anneau des nacelles. On le traverse en marchant,
    // à condition de ne pas être resté sous le boss.
    spreadDeg: 168,
    speed: 320,
    firstDelay: firstDelayAt(PHASE_NOYAU),
    family: Fam.EnemyRed,
  },

  /* ── L'EXAMEN, en trois phases ─────────────────────────────────────────────
     Le noyau est `shieldedByParts` : ses PV ne baissent QU'UNE FOIS les six armes
     démontées. Ces phases se jouent donc dans le FINALE — précisément là où le
     commentaire d'origine redoutait « un sac de points de vie parfaitement
     inerte ». Au lieu de répéter son grand éventail lent jusqu'au bout, le noyau,
     débarrassé de ses bras, se déchaîne : il PULSE (anneaux), puis SPIRALE. Chaque
     bascule est re-télégraphiée (il se fige, devient un instant intouchable) et
     accélère un peu. La densité reste basse — une seule source, cap monde 1 à 90.
     Les seuils sont sur les PV du CORPS (18 000), donc sur ce finale seul. */
  phases: [
    {
      // DERNIÈRE ARME TOMBÉE (6 parties détruites) : le noyau s'éveille À L'INSTANT
      // où il devient touchable — plus de long éventail mort au début du finale. Il
      // pulse : des anneaux qui tournent, le volume, mais mobile.
      atPartsLost: 6,
      telegraph: 0.8,
      speedMul: 1.15,
      emitter: { pattern: Pattern.Ring, every: 1.9, count: 14, speed: 265, stepDeg: 13 },
    },
    {
      // 33 % : la spirale. Une géométrie qui se déroule — « esquive où elle SERA ».
      atHpFrac: 0.33,
      telegraph: 1.0,
      speedMul: 1.15,
      emitter: {
        pattern: Pattern.Spiral,
        every: 0.42,
        count: 3,
        speed: 240,
        stepDeg: 27,
        turnDeg: 30,
      },
    },
  ],
  onUpdate: piloter,

  /* ── Les six parties ──────────────────────────────────────────────────────
     L'ordre de ce tableau est l'ordre d'allocation des slots. Il n'a aucune
     incidence sur le jeu, mais il en a une sur la lisibilité d'un dump de pool
     pendant un débogage : on les déclare de l'extérieur vers l'intérieur.

     ⚠️ Les ordonnées. Le point le plus bas est un bras à offY -110, soit
     Y = +220 une fois le porteur posé à +330. L'invariant « aucun tir sous
     Y = -180 » (PLAN.md §6.6) est donc respecté avec 400 u de marge, et il l'est
     STRUCTURELLEMENT : le porteur ne descend plus après sa station, et le hook
     ne touche pas à Y. */
  parts: [
    { enemy: 'heraut-bras', offX: -200, offY: -110 },
    { enemy: 'heraut-bras', offX: 200, offY: -110 },
    { enemy: 'heraut-canon', offX: -118, offY: -62 },
    { enemy: 'heraut-canon', offX: 118, offY: -62 },
    { enemy: 'heraut-nacelle', offX: -58, offY: 42 },
    { enemy: 'heraut-nacelle', offX: 58, offY: 42 },
  ],

  /* Le noyau est intouchable tant qu'une partie survit.
   *
   *  C'est ce qui transforme 18 000 PV en combat structuré plutôt qu'en
   *  attrition. Le joueur ne choisit pas S'IL démonte le boss, il choisit DANS
   *  QUEL ORDRE — et cet ordre est sa difficulté :
   *
   *    canons d'abord   2 200 PV de terrain rendu, mais deux salves visées de
   *                     plus à encaisser. Long et sûr.
   *    bras d'abord     3 000 PV pour supprimer la seule menace dirigée, sous
   *                     un quadrillage qui ne faiblit pas. Court et nerveux.
   *    nacelles d'abord 1 040 PV, la voie de la panique : ça meurt vite, ça ne
   *                     retire presque rien.
   *
   *  Et la dégradation va dans le bon sens : si le pool sature et qu'aucune
   *  partie n'apparaît, `livingParts` renvoie zéro et le noyau redevient
   *  vulnérable. Un boss diminué, jamais un boss invincible. */
  shieldedByParts: true,
});
